import { useCallback } from 'react';
import type { ParamListBase } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useSafeFocusEffect, useSafeRouter } from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useMenuRedirectQuery } from '../../GraphQL/MenuRedirect.generated';

import type { AppStackParamList } from '../../../../navigation/AppNavigation.props';

// Here we handle the short menu link redirects (/west-3rd -> /west-3rd/menu).
export const useMenuRedirect = (skip: boolean, params: MenuRedirectParams) => {
  const { navigation, restaurantSlug } = params;

  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const router = useSafeRouter();

  const [restaurantQueryResponse] = useMenuRedirectQuery({
    variables: { id: restaurantSlug ?? '' },
    requestPolicy: 'cache-and-network',
    pause: !restaurantSlug || skip,
  });

  const isShortMenuLink = Boolean(restaurantQueryResponse?.data?.restaurant);

  useSafeFocusEffect(
    useCallback(() => {
      if (!isShortMenuLink || !restaurantSlug) return;

      if (isExpoRouterEnabled) {
        router?.replace({
          pathname: '/[restaurantSlug]/menu',
          params: { restaurantSlug },
        });

        return;
      }

      const redirectTo = getMenuRedirect(restaurantSlug);

      navigation?.replace(redirectTo.screen, redirectTo.params);
    }, [isShortMenuLink, restaurantSlug, isExpoRouterEnabled, router, navigation]),
  );

  return restaurantQueryResponse.fetching;
};

const getMenuRedirect = (restaurantSlug: string): MenuRedirect => {
  return {
    screen: 'MainTabs',
    params: {
      screen: 'MenuTab',
      params: {
        screen: 'Menu',
        params: { restaurantSlug },
      },
    },
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuRedirectParams = {
  navigation: NativeStackNavigationProp<ParamListBase> | null | undefined;
  restaurantSlug: string | undefined;
};

type MenuRedirect = Readonly<{
  screen: keyof AppStackParamList;
  params: AppStackParamList[keyof AppStackParamList];
}>;
